import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { LayoutDashboard, Users, Clock, FileSpreadsheet, Settings, LogOut, Plus, Trash2, Pencil, Download, Copy, Monitor, Smartphone, MapPin, Palette, KeyRound, Timer, DollarSign, Coffee, Camera, UserPlus, Printer, LogIn as LogInIcon, Plug, RefreshCw, CheckCircle2, Link2Off, Eye, ChevronDown, CalendarClock, Send, MessageSquare, Megaphone, AlertTriangle, Repeat, Ban, QrCode, History, ClipboardCheck, Search, UploadCloud } from "lucide-react";
import { api, apiError, fileUrl, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Brand";
import { ApprovalControls } from "../components/ApprovalControls";
import { printBadge, BADGE_LABELS } from "../lib/badge";
import { NotificationBell } from "../components/NotificationBell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { fmtDateTime, fmtTime, fmtDateStr, UNIT_ABBR } from "../lib/format";
import Schedules from "./admin/Schedules";
import Messages from "./admin/Messages";
import Announcements from "./admin/Announcements";
import { QRCodeCanvas } from "qrcode.react";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "employees", label: "Employees", icon: Users },
  { key: "timesheets", label: "Timesheets", icon: Clock },
  { key: "audit", label: "Audit trail", icon: History },
  { key: "schedules", label: "Schedules", icon: CalendarClock },
  { key: "reports", label: "Reports", icon: FileSpreadsheet },
  { key: "approvals", label: "Approvals", icon: CheckCircle2 },
  { key: "approval-requests", label: "Approval requests", icon: ClipboardCheck },
  { key: "visitors", label: "Visitors", icon: UserPlus },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "announcements", label: "Announcements", icon: Megaphone },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "settings", label: "Settings", icon: Settings },
];

function fmtHrs(sec) {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}
function money(n, cur = "USD") {
  try { return new Intl.NumberFormat([], { style: "currency", currency: cur }).format(n); }
  catch { return `$${n.toFixed(2)}`; }
}
function copy(t, l = "Copied") { navigator.clipboard.writeText(t); toast.success(l); }

export default function CompanyAdmin() {
  const { user, logout, company: authCompany, setCompany, impersonating, exitImpersonation, actingCompany, clearActingCompany } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [company, setCompanyState] = useState(null);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const loadCompany = useCallback(() => api.get("/company").then(({ data }) => { setCompanyState(data); setCompany(data); }).catch(() => {}), [setCompany]);
  const loadStats = useCallback(() => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const loadEmployees = useCallback(() => api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {}), []);
  const loadPending = useCallback(() => api.get("/approvals/requests").then(({ data }) => setPendingApprovals((data.requests || []).filter((r) => r.status === "pending").length)).catch(() => {}), []);
  const loadUnread = useCallback(() => { const cid = company?.id; if (!cid) return; api.get(`/messages?company_id=${cid}`).then(({ data }) => setUnreadMessages(data.unread || 0)).catch(() => {}); }, [company]);

  useEffect(() => { loadCompany(); loadStats(); loadEmployees(); loadPending(); }, [loadCompany, loadStats, loadEmployees, loadPending]);
  useEffect(() => { loadUnread(); }, [loadUnread]);

  // Live unread badge: listen to the messaging websocket and bump the count the
  // moment a processor replies, even while the admin is on another tab.
  const tabRef = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  useEffect(() => {
    const cid = company?.id;
    if (!cid) return undefined;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let ws;
    try {
      ws = new WebSocket(`${base}/api/ws/messages/${cid}?token=${localStorage.getItem("cs_token")}`);
      ws.onmessage = (ev) => {
        let d; try { d = JSON.parse(ev.data); } catch { return; }
        if (d.type === "message" && d.message?.sender_id !== user?.id && tabRef.current !== "messages") loadUnread();
      };
    } catch { /* the 15s poll in loadUnread callers covers it */ }
    return () => { try { if (ws) ws.close(); } catch { /* noop */ } };
  }, [company, user, loadUnread]);

  useEffect(() => { if (company) document.documentElement.style.setProperty("--tenant-accent", company.primary_color); }, [company]);

  const showBanner = impersonating || (user?.role === "processor" && actingCompany);
  const exitBanner = async () => {
    if (impersonating) { await exitImpersonation(); navigate("/super"); }
    else { clearActingCompany(); navigate("/processor"); }
  };
  const visibleTabs = (user?.role === "processor" && user?.permissions?.length) ? TABS.filter((t) => user.permissions.includes(t.key)) : TABS;
  useEffect(() => { if (visibleTabs.length && !visibleTabs.some((t) => t.key === tab)) setTab(visibleTabs[0].key); }, [visibleTabs, tab]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-slate-900 border-r border-slate-800 p-4 shrink-0 flex md:flex-col justify-between md:min-h-screen">
        <div className="w-full">
          <div className="px-2 py-2 mb-4"><Logo size="sm" logoPath={company?.logo_path} companyName={company?.name} showText /></div>
          <nav className="flex md:flex-col gap-1 overflow-x-auto">
            {visibleTabs.map((t) => (
              <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => { const prev = tab; setTab(t.key); if (t.key === "approval-requests") loadPending(); if (t.key === "messages") setUnreadMessages(0); else if (prev === "messages") loadUnread(); }}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === t.key ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800"}`}>
                <t.icon className="w-4 h-4" /> <span className="flex-1 text-left">{t.label}</span>
                {t.key === "approval-requests" && pendingApprovals > 0 && (
                  <span data-testid="approval-pending-badge" className={`ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-bold ${tab === t.key ? "bg-white/25 text-white" : "bg-amber-500 text-slate-950"}`}>{pendingApprovals}</span>
                )}
                {t.key === "messages" && unreadMessages > 0 && (
                  <span data-testid="messages-unread-badge" className={`ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-bold ${tab === t.key ? "bg-white/25 text-white" : "bg-rose-500 text-white"}`}>{unreadMessages}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
        <div className="hidden md:block">
          <div className="text-xs text-slate-500 px-4 mb-2 truncate">{user?.email}</div>
          <button data-testid="admin-logout-button" onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 sm:p-8 overflow-y-auto">
        {showBanner && (
          <div className="max-w-6xl mx-auto mb-3">
            <div data-testid="impersonation-banner" className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-xl px-4 py-2.5 text-sm">
              <span>{impersonating ? `Viewing as ${company?.name || "this company"} — impersonation active` : `Managing ${actingCompany?.name}`}</span>
              <button data-testid="exit-impersonation-button" onClick={exitBanner} className="font-semibold underline hover:text-amber-200">{impersonating ? "Exit impersonation" : "Back to my companies"}</button>
            </div>
          </div>
        )}
        <div className="max-w-6xl mx-auto flex justify-end items-center gap-3 mb-2"><ThemeToggle /><NotificationBell /></div>
        <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="max-w-6xl mx-auto">
          {tab === "overview" && <Overview stats={stats} company={company} employees={employees} onNav={setTab} pendingApprovals={pendingApprovals} unreadMessages={unreadMessages} />}
          {tab === "employees" && <Employees employees={employees} reload={() => { loadEmployees(); loadStats(); }} currency={company?.currency} />}
          {tab === "timesheets" && <Timesheets />}
          {tab === "audit" && <AuditTrail />}
          {tab === "schedules" && <Schedules employees={employees} company={company} />}
          {tab === "reports" && <Reports currency={company?.currency} company={company} />}
          {tab === "approvals" && <Approvals currency={company?.currency} />}
          {tab === "approval-requests" && <ApprovalsInbox company={company} onChange={loadPending} />}
          {tab === "visitors" && company && <Visitors company={company} />}
          {tab === "messages" && company && <Messages companyId={company.id} title="Messages with your payroll processor" />}
          {tab === "announcements" && company && <Announcements employees={employees} />}
          {tab === "integrations" && company && <Integrations company={company} />}
          {tab === "settings" && company && <SettingsTab company={company} reload={loadCompany} />}
        </motion.div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid, onClick }) {
  return (
    <div data-testid={testid} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
      className={`bg-slate-900/80 border border-slate-800 rounded-2xl p-5 transition-colors ${onClick ? "cursor-pointer hover:border-blue-500/50 hover:bg-slate-900 active:scale-[0.98]" : ""}`}>
      <div className="flex items-center gap-2 text-slate-400 text-sm"><Icon className={`w-4 h-4 ${accent}`} /> {label}</div>
      <div className="text-3xl font-extrabold mt-2 font-mono">{value}</div>
    </div>
  );
}

function HealthTile({ icon: Icon, label, value, danger, onClick, testid }) {
  return (
    <div data-testid={testid} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      className={`rounded-xl border px-4 py-3 cursor-pointer transition-colors active:scale-[0.98] ${danger ? "bg-amber-500/10 border-amber-500/40 hover:border-amber-400" : "bg-slate-950 border-slate-800 hover:border-blue-500/50"}`}>
      <div className={`flex items-center gap-1.5 text-xs ${danger ? "text-amber-300" : "text-slate-400"}`}><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className={`text-2xl font-extrabold mt-1 font-mono ${danger ? "text-amber-300" : "text-slate-200"}`}>{value}</div>
    </div>
  );
}

function Overview({ stats, company, employees, onNav, pendingApprovals = 0, unreadMessages = 0 }) {
  const [health, setHealth] = useState(null);
  const [processors, setProcessors] = useState([]);
  useEffect(() => { api.get("/schedules/health").then(({ data }) => setHealth(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/company/processor").then(({ data }) => setProcessors(data.processors || [])).catch(() => {}); }, []);
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Overview</h1>
          <p className="text-slate-400 text-sm">Live snapshot for {company?.name}{company?.account_number && <span data-testid="overview-account-number" className="ml-2 inline-flex items-center rounded-full bg-slate-800 border border-slate-700 px-2.5 py-0.5 text-xs font-mono text-slate-300">Account #: {company.account_number}</span>}</p>
        </div>
        {processors.length > 0 && (
          <div data-testid="overview-processor-card" className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3 shrink-0 sm:min-w-[280px] sm:max-w-[340px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{processors.length > 1 ? `Your payroll processors (${processors.length})` : "Your payroll processor"}</div>
            <div className="space-y-3">
              {processors.map((p) => (
                <div key={p.email || p.name} data-testid="overview-processor-item" className="flex items-center gap-3">
                  {p.photo_path
                    ? <img src={fileUrl(p.photo_path)} alt={p.name} className="w-11 h-11 rounded-full object-cover border border-slate-700 shrink-0" />
                    : <div className="w-11 h-11 rounded-full bg-blue-500/15 flex items-center justify-center text-blue-300 font-bold shrink-0">{(p.name || "?").slice(0, 1).toUpperCase()}</div>}
                  <div className="text-sm min-w-0">
                    <div className="font-semibold truncate" data-testid="processor-name">{p.name}</div>
                    {p.title && <div className="text-xs text-slate-400 truncate">{p.title}</div>}
                    {p.phone && <a href={`tel:${p.phone.replace(/\D/g, "")}`} className="text-xs text-blue-400 hover:text-blue-300 block">{p.phone}</a>}
                    {p.email && <a href={`mailto:${p.email}`} className="text-xs text-blue-400 hover:text-blue-300 block truncate">{p.email}</a>}
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="processor-message-button" onClick={() => onNav("messages")} className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-600 hover:bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors">
              <MessageSquare className="w-3.5 h-3.5" /> Message
              {unreadMessages > 0 && <span data-testid="processor-message-unread" className="ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">{unreadMessages}</span>}
            </button>
          </div>
        )}
      </div>

      {pendingApprovals > 0 && (
        <button data-testid="overview-approvals-awaiting" onClick={() => onNav("approval-requests")}
          className="w-full mb-6 flex items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15 px-5 py-4 text-left transition-colors">
          <span className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center"><ClipboardCheck className="w-5 h-5 text-amber-300" /></span>
            <span>
              <span className="block font-semibold text-amber-200">{pendingApprovals} approval{pendingApprovals === 1 ? "" : "s"} awaiting response</span>
              <span className="block text-xs text-amber-300/70">Schedule &amp; payroll requests waiting on an approver — review or resend now.</span>
            </span>
          </span>
          <span className="text-sm font-semibold text-amber-200 whitespace-nowrap">Review →</span>
        </button>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Employees" value={stats?.total_employees ?? "—"} accent="text-blue-400" testid="stat-employees" onClick={() => onNav("employees")} />
        <StatCard icon={Timer} label="On the clock" value={stats?.on_clock ?? "—"} accent="text-emerald-400" testid="stat-onclock" onClick={() => onNav("timesheets")} />
        <StatCard icon={Coffee} label="On break" value={stats?.on_break ?? "—"} accent="text-amber-400" testid="stat-onbreak" onClick={() => onNav("timesheets")} />
        <StatCard icon={DollarSign} label="14d gross" value={stats ? money(stats.period_gross, stats.currency) : "—"} accent="text-blue-400" testid="stat-gross" onClick={() => onNav("reports")} />
      </div>

      <div className="mt-6 bg-slate-900/80 border border-slate-800 rounded-2xl p-5" data-testid="schedule-health-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2"><CalendarClock className="w-4 h-4 text-blue-400" /> Schedule health <span className="text-xs text-slate-500 font-normal">(next 14 days)</span></h3>
          <button data-testid="health-open-schedules" onClick={() => onNav("schedules")} className="text-xs text-blue-400 hover:text-blue-300">Open schedules →</button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <HealthTile label="Uncovered days" value={health?.uncovered_count ?? "—"} danger={(health?.uncovered_count || 0) > 0} icon={CalendarClock} onClick={() => onNav("schedules")} testid="health-uncovered" />
          <HealthTile label="Overtime flags" value={health?.overtime_count ?? "—"} danger={(health?.overtime_count || 0) > 0} icon={AlertTriangle} onClick={() => onNav("schedules")} testid="health-overtime" />
          <HealthTile label="Pending swaps" value={health?.pending_swaps ?? "—"} danger={(health?.pending_swaps || 0) > 0} icon={Repeat} onClick={() => onNav("schedules")} testid="health-swaps" />
          <HealthTile label="Unavailable hits" value={health?.availability_conflict_count ?? "—"} danger={(health?.availability_conflict_count || 0) > 0} icon={Ban} onClick={() => onNav("schedules")} testid="health-availability" />
        </div>
        {health && (health.uncovered_count + health.overtime_count + health.pending_swaps + health.availability_conflict_count === 0) && (
          <p className="text-xs text-emerald-400 mt-3" data-testid="health-all-clear">All clear — no coverage gaps, overtime, swaps or conflicts in the next two weeks.</p>
        )}
      </div>

      <div className="mt-6 bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
        <h3 className="font-semibold mb-4">Employee roster</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {employees.map((e) => (
            <div key={e.id} onClick={() => onNav("employees")} role="button" tabIndex={0} data-testid={`roster-card-${e.id}`}
              onKeyDown={(ev) => { if (ev.key === "Enter") onNav("employees"); }}
              className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 cursor-pointer hover:border-blue-500/50 transition-colors active:scale-[0.98]">
              <div>
                <div className="font-medium">{e.name}</div>
                <div className="text-xs text-slate-500 font-mono">{money(e.hourly_rate, company?.currency)}/hr</div>
              </div>
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${e.active ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-800 text-slate-500"}`}>{e.active ? "active" : "off"}</span>
            </div>
          ))}
          {employees.length === 0 && <p className="text-slate-500 text-sm">No employees yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Employees({ employees, reload, currency }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewE, setViewE] = useState(null);
  const [form, setForm] = useState({ name: "", pin: "", email: "", title: "", organization: "", employee_number: "", photo_data: null, photo_path: null, rates: [{ label: "Standard", rate: "" }] });
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRows, setEmailRows] = useState([]);

  const openNew = () => { setEditing(null); setForm({ name: "", pin: "", email: "", title: "", organization: "", employee_number: "", photo_data: null, photo_path: null, rates: [{ label: "Standard", rate: "" }] }); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name, pin: "", email: e.email || "", title: e.title || "", organization: e.organization || "", employee_number: e.employee_number || "", photo_data: null, photo_path: e.photo_path || null,
      rates: (e.pay_rates && e.pay_rates.length) ? e.pay_rates.map((r) => ({ label: r.label, rate: String(r.rate) })) : [{ label: "Standard", rate: String(e.hourly_rate ?? "") }],
    });
    setOpen(true);
  };

  const onPhoto = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo_data: reader.result }));
    reader.readAsDataURL(file);
  };
  const setRate = (i, key, val) => setForm((f) => ({ ...f, rates: f.rates.map((r, idx) => idx === i ? { ...r, [key]: val } : r) }));
  const addRate = () => setForm((f) => ({ ...f, rates: [...f.rates, { label: "", rate: "" }] }));
  const removeRate = (i) => setForm((f) => ({ ...f, rates: f.rates.filter((_, idx) => idx !== i) }));

  const save = async () => {
    try {
      const pay_rates = form.rates.filter((r) => r.label.trim() && r.rate !== "").map((r) => ({ label: r.label.trim(), rate: parseFloat(r.rate) || 0 }));
      if (pay_rates.length === 0) { toast.error("Add at least one pay rate"); return; }
      const payload = { name: form.name, email: form.email || null, title: form.title || null, organization: form.organization || null, employee_number: form.employee_number || null, pay_rates };
      if (form.photo_data) payload.photo_data = form.photo_data;
      if (form.pin) payload.pin = form.pin;
      if (editing) {
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        if (!form.pin) { toast.error("PIN required"); return; }
        await api.post("/employees", { ...payload, pin: form.pin });
        toast.success("Employee added");
      }
      setOpen(false); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this employee?")) return;
    await api.delete(`/employees/${id}`); toast.success("Removed"); reload();
  };
  const openEmailImport = () => { setEmailRows(employees.map((e) => ({ id: e.id, name: e.name, email: e.email || "" }))); setEmailOpen(true); };
  const saveEmails = async () => {
    try {
      const { data } = await api.post("/employees/bulk-email", { items: emailRows.map((r) => ({ id: r.id, email: r.email })) });
      toast.success(`Updated ${data.updated} employee${data.updated !== 1 ? "s" : ""}`); setEmailOpen(false); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const Avatar = ({ e, size = "w-9 h-9" }) => (
    e.photo_path
      ? <img src={fileUrl(e.photo_path)} alt={e.name} className={`${size} rounded-full object-cover border border-slate-700`} />
      : <div className={`${size} rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-bold text-slate-300`}>{e.name[0]}</div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Employees</h1><p className="text-slate-400 text-sm mt-1">{employees.length} on staff</p></div>
        <div className="flex items-center gap-2">
          <button data-testid="import-emails-button" onClick={openEmailImport} className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 rounded-full px-4 py-2.5 font-semibold transition-colors">
            <Download className="w-4 h-4" /> Import emails
          </button>
          <button data-testid="add-employee-button" onClick={openNew} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 rounded-full px-5 py-2.5 font-semibold transition-colors">
            <Plus className="w-4 h-4" /> Add employee
          </button>
        </div>
      </div>
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">Employee</th><th className="text-left px-5 py-3">Rates</th><th className="text-left px-5 py-3 hidden sm:table-cell">Email</th><th className="text-right px-5 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id} className="border-t border-slate-800" data-testid={`employee-row-${e.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3"><Avatar e={e} /><div><span className="font-medium">{e.name}</span>{e.employee_number && <span className="ml-2 text-[11px] font-mono text-blue-400" data-testid={`employee-number-${e.id}`}>#{e.employee_number}</span>}{e.title && <div className="text-xs text-slate-500">{e.title}{e.organization ? ` · ${e.organization}` : ""}</div>}</div></div>
                </td>
                <td className="px-5 py-3 font-mono text-slate-300">
                  {(e.pay_rates && e.pay_rates.length ? e.pay_rates : [{ label: "Standard", rate: e.hourly_rate }]).map((r) => (
                    <span key={r.label} className="inline-block mr-2 text-xs bg-slate-800 rounded-md px-2 py-0.5">{r.label}: {money(r.rate, currency)}</span>
                  ))}
                </td>
                <td className="px-5 py-3 text-slate-400 hidden sm:table-cell">{e.email || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button data-testid={`view-employee-${e.id}`} onClick={() => setViewE(e)} className="p-2 text-slate-400 hover:text-slate-100 transition-colors"><Eye className="w-4 h-4" /></button>
                  <button data-testid={`edit-employee-${e.id}`} onClick={() => openEdit(e)} className="p-2 text-slate-400 hover:text-blue-400 transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-employee-${e.id}`} onClick={() => remove(e.id)} className="p-2 text-slate-400 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {employees.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No employees yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center">
                {form.photo_data || form.photo_path
                  ? <img src={form.photo_data || fileUrl(form.photo_path)} alt="employee" className="w-full h-full object-cover" />
                  : <Camera className="w-6 h-6 text-slate-500" />}
              </div>
              <label className="cursor-pointer bg-slate-800 hover:bg-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors">
                Upload picture
                <input data-testid="employee-photo-input" type="file" accept="image/*" onChange={onPhoto} className="hidden" />
              </label>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Full name</label>
              <input data-testid="employee-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-300">Employee number</label>
                <input data-testid="employee-number-input" value={form.employee_number} onChange={(e) => setForm((f) => ({ ...f, employee_number: e.target.value }))} placeholder="e.g. EMP-1042"
                  className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 font-mono focus:border-blue-500 outline-none transition" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">Title</label>
                <input data-testid="employee-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Barista"
                  className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">Organization</label>
                <input data-testid="employee-organization-input" value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} placeholder="Optional"
                  className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">PIN {editing && <span className="text-slate-500">(leave blank to keep)</span>}</label>
              <input data-testid="employee-pin-input" inputMode="numeric" value={form.pin} onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, "") }))} placeholder="4-8 digits"
                className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 font-mono tracking-widest focus:border-blue-500 outline-none transition" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-300">Pay rates</label>
                <button data-testid="add-rate-button" onClick={addRate} className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add rate</button>
              </div>
              <div className="mt-1.5 space-y-2">
                {form.rates.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input data-testid={`rate-label-${i}`} placeholder="Label (e.g. Weekend)" value={r.label} onChange={(e) => setRate(i, "label", e.target.value)}
                      className="flex-1 rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm focus:border-blue-500 outline-none transition" />
                    <input data-testid={`rate-value-${i}`} type="number" step="0.01" placeholder="0.00" value={r.rate} onChange={(e) => setRate(i, "rate", e.target.value)}
                      className="w-24 rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none transition" />
                    {form.rates.length > 1 && <button onClick={() => removeRate(i)} className="p-1.5 text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">First rate is the default. Staff pick a rate at clock-in when multiple exist.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Email (optional)</label>
              <input data-testid="employee-email-input" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-employee-button" onClick={save} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold transition-colors">{editing ? "Save changes" : "Add employee"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewE} onOpenChange={(o) => !o && setViewE(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="employee-view-modal">
          <DialogHeader><DialogTitle>Employee details</DialogTitle></DialogHeader>
          {viewE && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-4">
                <Avatar e={viewE} size="w-16 h-16" />
                <div><div className="text-lg font-bold">{viewE.name}</div><div className="text-sm text-slate-400">{viewE.title || "—"}{viewE.organization ? ` · ${viewE.organization}` : ""}</div></div>
              </div>
              <div className="text-sm"><span className="text-slate-400">Employee #: </span>{viewE.employee_number || "—"}</div>
              <div className="text-sm"><span className="text-slate-400">Email: </span>{viewE.email || "—"}</div>
              <div className="text-sm"><span className="text-slate-400">Status: </span>{viewE.active ? "Active" : "Inactive"}</div>
              <div className="text-sm text-slate-400">Pay rates</div>
              <div className="flex flex-wrap gap-2">
                {(viewE.pay_rates && viewE.pay_rates.length ? viewE.pay_rates : [{ label: "Standard", rate: viewE.hourly_rate }]).map((r) => (
                  <span key={r.label} className="text-xs bg-slate-800 rounded-md px-2 py-1 font-mono">{r.label}: {money(r.rate, currency)}</span>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <button data-testid="view-edit-employee-button" onClick={() => { const e = viewE; setViewE(null); openEdit(e); }} className="w-full rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 font-semibold transition-colors inline-flex items-center justify-center gap-2"><Pencil className="w-4 h-4" /> Edit</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="import-emails-modal">
          <DialogHeader><DialogTitle>Import employee emails</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400 -mt-1">Add or update emails so Publish &amp; Notify and Announcements reach your team.</p>
          <div className="space-y-2 py-2 max-h-[55vh] overflow-y-auto">
            {emailRows.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3" data-testid={`email-row-${r.id}`}>
                <span className="text-sm text-slate-300 w-32 truncate shrink-0">{r.name}</span>
                <input data-testid={`email-input-${r.id}`} type="email" value={r.email} placeholder="name@company.com"
                  onChange={(e) => setEmailRows((rows) => rows.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x))}
                  className="flex-1 rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm focus:border-blue-500 outline-none transition" />
              </div>
            ))}
            {emailRows.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No employees yet.</p>}
          </div>
          <DialogFooter>
            <button data-testid="save-emails-button" onClick={saveEmails} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold transition-colors">Save emails</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DateRange({ from, to, setFrom, setTo }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <input data-testid="range-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-lg bg-slate-950 border border-slate-700 px-3 py-2 focus:border-blue-500 outline-none" />
      <span className="text-slate-500">to</span>
      <input data-testid="range-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-lg bg-slate-950 border border-slate-700 px-3 py-2 focus:border-blue-500 outline-none" />
    </div>
  );
}

function useDefaultRange() {
  const today = new Date();
  const past = new Date(); past.setDate(today.getDate() - 14);
  const iso = (d) => d.toISOString().slice(0, 10);
  return [iso(past), iso(today)];
}

function Timesheets() {
  const [df, dt] = useDefaultRange();
  const [from, setFrom] = useState(df);
  const [to, setTo] = useState(dt);
  const [shifts, setShifts] = useState([]);
  const [edit, setEdit] = useState(null); // shift being edited
  const [startVal, setStartVal] = useState("");
  const [endVal, setEndVal] = useState("");
  const [savingTs, setSavingTs] = useState(false);

  const load = useCallback(() => {
    api.get(`/timesheets?from=${from}&to=${to}`).then(({ data }) => setShifts(data.shifts)).catch(() => {});
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const toInput = (iso) => { if (!iso) return ""; const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
  const fromInput = (v) => new Date(v).toISOString();

  const openEdit = (s) => { setEdit(s); setStartVal(toInput(s.start)); setEndVal(s.end ? toInput(s.end) : ""); };
  const saveEdit = async () => {
    if (!edit) return;
    setSavingTs(true);
    try {
      if (startVal && edit.in_event_id && toInput(edit.start) !== startVal) {
        await api.put(`/timesheets/event/${edit.in_event_id}`, { timestamp: fromInput(startVal) });
      }
      if (endVal) {
        if (edit.out_event_id) {
          if (toInput(edit.end) !== endVal) await api.put(`/timesheets/event/${edit.out_event_id}`, { timestamp: fromInput(endVal) });
        } else {
          await api.post(`/timesheets/add-out`, { employee_id: edit.employee_id, timestamp: fromInput(endVal) });
        }
      }
      toast.success("Timesheet updated");
      setEdit(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingTs(false); }
  };
  const deleteShift = async () => {
    if (!edit || !window) return;
    if (!window.confirm("Delete this punch entirely?")) return;
    try {
      if (edit.in_event_id) await api.delete(`/timesheets/event/${edit.in_event_id}`);
      if (edit.out_event_id) await api.delete(`/timesheets/event/${edit.out_event_id}`);
      toast.success("Punch removed"); setEdit(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const missed = shifts.filter((s) => s.missed);
  const inputCls = "mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl font-bold">Timesheets</h1><p className="text-slate-400 text-sm mt-1">{shifts.length} shifts in range · click a row to edit</p></div>
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
      </div>

      {missed.length > 0 && (
        <div className="mb-5 bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4" data-testid="missed-punches">
          <div className="flex items-center gap-2 font-semibold text-amber-300 mb-2"><AlertTriangle className="w-4 h-4" /> Missed punches ({missed.length})</div>
          <p className="text-xs text-amber-200/80 mb-3">These employees clocked in but never clocked out. Add their clock-out time to fix the timesheet.</p>
          <div className="space-y-2">
            {missed.map((s) => (
              <div key={s.in_event_id} className="flex items-center justify-between bg-slate-950/50 border border-amber-500/20 rounded-xl px-4 py-2.5" data-testid={`missed-row-${s.in_event_id}`}>
                <div className="text-sm">
                  <span className="font-medium text-slate-200">{s.employee_name}</span>
                  <span className="text-slate-400 ml-2 font-mono text-xs">in {fmtDateTime(s.start)} · no clock-out</span>
                </div>
                <button data-testid={`fix-missed-${s.in_event_id}`} onClick={() => openEdit(s)} className="rounded-lg bg-amber-600 hover:bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white transition-colors">Add clock-out</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="timesheets-groups">
        {Object.entries(shifts.reduce((acc, s) => { (acc[s.employee_name] = acc[s.employee_name] || []).push(s); return acc; }, {})).map(([name, list]) => (
          <details key={name} data-testid={`ts-group-${name}`} open className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
            <summary className="flex items-center justify-between px-5 py-3 cursor-pointer select-none list-none">
              <span className="font-semibold flex items-center gap-2"><ChevronDown className="w-4 h-4 text-slate-500" /> {name}</span>
              <span className="text-xs font-mono text-slate-400">{list.length} shifts · {fmtHrs(list.reduce((t, x) => t + x.worked_seconds, 0))}</span>
            </summary>
            <table className="w-full text-sm border-t border-slate-800">
              <tbody>
                {list.map((s) => (
                  <tr key={s.in_event_id} className="border-t border-slate-800/60 hover:bg-slate-800/30 transition-colors" data-testid={`ts-row-${s.in_event_id}`}>
                    <td className="px-5 py-2.5 font-mono text-slate-300">{fmtDateTime(s.start)}{s.edited && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300" title={s.edited_by_name ? `Edited by ${s.edited_by_name}${s.edited_at ? " · " + fmtDateTime(s.edited_at) : ""}` : "Edited"}>edited</span>}</td>
                    <td className="px-5 py-2.5 font-mono text-slate-300">{s.missed ? <span className="text-amber-400">⚠ no clock-out</span> : s.open ? <span className="text-emerald-400">● active</span> : fmtTime(s.end)}</td>
                    <td className="px-5 py-2.5 font-mono text-slate-400">break {fmtHrs(s.break_seconds)}</td>
                    <td className="px-5 py-2.5 font-mono font-semibold">{fmtHrs(s.worked_seconds)}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-400">{s.rate_label || "—"}</td>
                    <td className="px-5 py-2.5">{s.source === "mobile" ? <Smartphone className="w-4 h-4 text-blue-400" /> : s.source === "admin" ? <Pencil className="w-4 h-4 text-slate-500" /> : <Monitor className="w-4 h-4 text-slate-400" />}</td>
                    <td className="px-5 py-2.5">{s.lat != null && s.lng != null ? <a href={`https://www.google.com/maps?q=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"><MapPin className="w-3.5 h-3.5" /> Map</a> : <span className="text-slate-700 text-xs">—</span>}</td>
                    <td className="px-5 py-2.5">{s.photo_path ? <a href={fileUrl(s.photo_path)} target="_blank" rel="noreferrer"><img src={fileUrl(s.photo_path)} alt="selfie" className="w-8 h-8 rounded-lg object-cover border border-slate-700" /></a> : <Camera className="w-4 h-4 text-slate-700" />}</td>
                    <td className="px-5 py-2.5 text-right"><button data-testid={`edit-ts-${s.in_event_id}`} onClick={() => openEdit(s)} className="p-2 text-slate-400 hover:text-blue-400 transition-colors"><Pencil className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ))}
        {shifts.length === 0 && <div className="bg-slate-900/80 border border-slate-800 rounded-2xl px-5 py-10 text-center text-slate-500">No shifts in this range.</div>}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="edit-timesheet-modal">
          <DialogHeader><DialogTitle>Edit timesheet — {edit?.employee_name}</DialogTitle></DialogHeader>
          {edit?.edited && (
            <div data-testid="edit-audit-log" className="flex items-start gap-2 rounded-xl bg-blue-500/10 border border-blue-500/25 text-blue-200 px-3 py-2 text-xs">
              <Pencil className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Last edited by <b>{edit.edited_by_name || "Admin"}</b>{edit.edited_at && <> on {fmtDateTime(edit.edited_at)}</>}</span>
            </div>
          )}
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-slate-300">Clock in</label>
              <input data-testid="edit-ts-start" type="datetime-local" value={startVal} onChange={(e) => setStartVal(e.target.value)} className={inputCls + " font-mono"} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Clock out {edit && !edit.out_event_id && <span className="text-amber-400 text-xs">(missing — add one)</span>}</label>
              <input data-testid="edit-ts-end" type="datetime-local" value={endVal} onChange={(e) => setEndVal(e.target.value)} className={inputCls + " font-mono"} />
            </div>
          </div>
          <DialogFooter>
            <div className="flex gap-2 w-full">
              <button data-testid="delete-ts-button" onClick={deleteShift} className="rounded-xl bg-rose-600/90 hover:bg-rose-500 px-4 py-2.5 font-semibold text-white transition-colors"><Trash2 className="w-4 h-4" /></button>
              <button data-testid="save-ts-button" onClick={saveEdit} disabled={savingTs} className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold disabled:opacity-60 transition-colors">{savingTs ? "Saving…" : "Save changes"}</button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Reports({ currency, company }) {
  const [df, dt] = useDefaultRange();
  const [from, setFrom] = useState(df);
  const [to, setTo] = useState(dt);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/payroll?from=${from}&to=${to}`).then(({ data }) => setData(data)).catch(() => {});
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/payroll/export?from=${from}&to=${to}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `payroll_${from}_${to}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const exportPunches = async () => {
    try {
      const res = await api.get(`/timesheets/punches.csv?from=${from}&to=${to}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `punches_${from}_${to}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Punch CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const chartData = (data?.rows || []).map((r) => ({ name: r.employee_name.split(" ")[0], Regular: r.regular_hours, Overtime: r.overtime_hours }));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl font-bold">Payroll reports</h1><p className="text-slate-400 text-sm mt-1">Hours, overtime & gross pay{data?.company_name && <> · {data.company_name}</>}{data?.account_number && <span data-testid="report-account-number" className="ml-2 font-mono text-slate-400">Account #: {data.account_number}</span>}</p></div>
        <div className="flex items-center gap-3">
          <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <button data-testid="punches-csv-export-button" onClick={exportPunches} className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 rounded-full px-5 py-2.5 font-semibold transition-colors">
            <Download className="w-4 h-4" /> Export punches
          </button>
          <button data-testid="payroll-csv-export-button" onClick={exportCsv} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 rounded-full px-5 py-2.5 font-semibold transition-colors">
            <Download className="w-4 h-4" /> Export summary
          </button>
          <ApprovalControls type="payroll" from={from} to={to} company={company} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <StatCard icon={Timer} label="Total hours" value={data ? `${data.total_hours}h` : "—"} accent="text-blue-400" testid="report-total-hours" />
        <StatCard icon={DollarSign} label="Total gross" value={data ? money(data.total_gross, currency) : "—"} accent="text-emerald-400" testid="report-total-gross" />
      </div>

      {chartData.length > 0 && (
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 mb-6">
          <h3 className="font-semibold mb-4">Hours by employee</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={{ background: "#0b1220", border: "1px solid #1f2937", borderRadius: 12, color: "#f8fafc" }} cursor={{ fill: "rgba(29,84,196,0.08)" }} />
              <Bar dataKey="Regular" stackId="a" fill="#1d54c4" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Overtime" stackId="a" fill="#f59e0b" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="payroll-table">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">Employee</th><th className="text-left px-5 py-3">Rate</th><th className="text-left px-5 py-3">Regular</th><th className="text-left px-5 py-3">OT</th><th className="text-left px-5 py-3">Total</th><th className="text-left px-5 py-3">Gross</th></tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.employee_id} className="border-t border-slate-800">
                <td className="px-5 py-3 font-medium">{r.employee_name}</td>
                <td className="px-5 py-3 font-mono text-slate-400">{money(r.hourly_rate, currency)}</td>
                <td className="px-5 py-3 font-mono">{r.regular_hours}h</td>
                <td className="px-5 py-3 font-mono text-amber-400">{r.overtime_hours}h</td>
                <td className="px-5 py-3 font-mono font-semibold">{r.total_hours}h</td>
                <td className="px-5 py-3 font-mono font-semibold text-emerald-400">{money(r.gross_pay, currency)}</td>
              </tr>
            ))}
            {(!data || data.rows.length === 0) && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No payroll data in this range.</td></tr>}
          </tbody>
        </table>
      </div>
      <RightPayHistory />
    </div>
  );
}

function RightPayHistory() {
  const [uploads, setUploads] = useState([]);
  useEffect(() => { api.get("/payroll/rightpay-uploads").then(({ data }) => setUploads(data.uploads || [])).catch(() => {}); }, []);
  if (uploads.length === 0) return null;
  return (
    <div className="mt-6" data-testid="rightpay-history">
      <h3 className="font-semibold mb-3 flex items-center gap-2"><UploadCloud className="w-4 h-4 text-indigo-400" /> RightPay upload history</h3>
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">Date</th><th className="text-left px-5 py-3">Period</th><th className="text-left px-5 py-3">Employees</th><th className="text-left px-5 py-3">Gross</th><th className="text-left px-5 py-3">Trigger</th><th className="text-left px-5 py-3">Status</th></tr>
          </thead>
          <tbody>
            {uploads.map((u) => (
              <tr key={u.id} className="border-t border-slate-800" data-testid={`rightpay-upload-${u.id}`}>
                <td className="px-5 py-3 text-slate-300 whitespace-nowrap">{fmtDateTime(u.created_at)}</td>
                <td className="px-5 py-3 font-mono text-slate-400 whitespace-nowrap">{fmtDateStr(u.period_start)} → {fmtDateStr(u.period_end)}</td>
                <td className="px-5 py-3">{u.employees}</td>
                <td className="px-5 py-3 font-mono text-emerald-400">{money(u.total_gross, u.currency)}</td>
                <td className="px-5 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.trigger === "auto" ? "bg-indigo-500/15 text-indigo-300" : "bg-slate-700/60 text-slate-300"}`}>{u.trigger === "auto" ? "Auto" : "Manual"}</span></td>
                <td className="px-5 py-3">{u.status === "success" ? <span className="text-emerald-400 text-xs">✓ success</span> : <span className="text-rose-400 text-xs" title={JSON.stringify(u.response)}>✗ failed</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditTrail() {
  const [entries, setEntries] = useState([]);
  useEffect(() => { api.get("/timesheets/audit").then(({ data }) => setEntries(data.entries)).catch(() => {}); }, []);
  const desc = (e) => {
    const which = e.punch_action === "in" ? "clock-in" : "clock-out";
    if (e.change_type === "delete") return `Deleted ${which} (${fmtDateTime(e.old_time)})`;
    if (e.change_type === "add_out") return `Added clock-out (${fmtDateTime(e.new_time)})`;
    return `Changed ${which} from ${fmtDateTime(e.old_time)} to ${fmtDateTime(e.new_time)}`;
  };
  return (
    <div>
      <div className="mb-6"><h1 className="text-2xl font-bold">Audit trail</h1><p className="text-slate-400 text-sm mt-1">Every timesheet edit, for payroll disputes</p></div>
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden" data-testid="audit-trail-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">When</th><th className="text-left px-5 py-3">Employee</th><th className="text-left px-5 py-3">Change</th><th className="text-left px-5 py-3">Changed by</th></tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-slate-800" data-testid={`audit-row-${e.id}`}>
                <td className="px-5 py-3 font-mono text-slate-400 whitespace-nowrap">{fmtDateTime(e.changed_at)}</td>
                <td className="px-5 py-3 font-medium">{e.employee_name}</td>
                <td className="px-5 py-3 text-slate-300">{desc(e)}</td>
                <td className="px-5 py-3 text-slate-400">{e.changed_by_name}</td>
              </tr>
            ))}
            {entries.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500" data-testid="audit-empty">No timesheet edits recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SETTINGS_SUBTABS = [
  { key: "branding", label: "Branding", icon: Palette },
  { key: "clock", label: "Kiosk & Clock", icon: Monitor },
  { key: "visitors", label: "Visitors", icon: UserPlus },
  { key: "payroll", label: "Payroll", icon: DollarSign },
  { key: "links", label: "Links & Help", icon: KeyRound },
];

const SettingsSection = ({ icon: Icon, title, children, group, active }) => {
  if (group && active && group !== active) return null;
  return (
  <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
    <h3 className="font-semibold flex items-center gap-2 mb-4"><Icon className="w-4 h-4 text-blue-400" /> {title}</h3>
    {children}
  </div>
  );
};
const Field = ({ label, children }) => (<div><label className="text-sm font-medium text-slate-300">{label}</label><div className="mt-1.5">{children}</div></div>);
const Toggle = ({ on, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`w-12 h-7 rounded-full transition-colors relative ${on ? "bg-blue-600" : "bg-slate-700"}`}>
    <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

function SettingsTab({ company, reload }) {
  const { theme, toggle } = useTheme();
  const [sub, setSub] = useState("branding");
  const [qrModal, setQrModal] = useState(null);
  const downloadQr = () => {
    const c = document.getElementById("qr-canvas");
    if (!c) return;
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `${(qrModal?.label || "qr").toLowerCase().replace(/\s+/g, "-")}.png`;
    a.click();
  };
  const printPoster = () => {
    const c = document.getElementById("qr-canvas");
    if (!c || !qrModal) return;
    const img = c.toDataURL("image/png");
    const logo = company.logo_path ? fileUrl(company.logo_path) : "";
    const accent = company.primary_color || "#1d54c4";
    const isPortal = qrModal.kind === "portal";
    const acct = (company.account_number || "").trim();
    const heading = isPortal ? "Scan to view your schedule" : "Scan to clock in";
    const sub = isPortal
      ? "Open your phone camera, scan the code, then enter your PIN to see your shifts."
      : "Open your phone camera and scan the code to clock in right from your phone.";
    const w = window.open("", "_blank", "width=820,height=1040");
    if (!w) { toast.error("Allow pop-ups to print the poster"); return; }
    w.document.write(`<!doctype html><html><head><title>${company.name} — ${heading}</title><meta charset="utf-8"/>
      <style>
        *{box-sizing:border-box;margin:0;padding:0;font-family:Arial,Helvetica,sans-serif}
        body{display:flex;align-items:center;justify-content:center;min-height:100vh;background:#fff;color:#0f172a}
        .poster{width:100%;max-width:640px;text-align:center;padding:0 40px 56px;border:4px solid ${accent};border-radius:28px;margin:24px;overflow:hidden}
        .header{background:${accent};margin:0 -40px 28px;padding:26px 24px;display:flex;flex-direction:column;align-items:center;gap:10px}
        .header .logo{max-height:70px;background:#fff;padding:8px 14px;border-radius:14px;display:block}
        .header .name{font-size:26px;font-weight:800;color:#fff;letter-spacing:.3px}
        .header .acct{font-size:13px;font-family:monospace;color:#fff;opacity:.85}
        h1{font-size:44px;font-weight:900;margin:14px 0}
        .qr{margin:28px auto;width:340px;height:340px}
        .qr img{width:100%;height:100%;image-rendering:pixelated}
        p{font-size:19px;color:#475569;margin-top:6px;line-height:1.4}
        .url{margin-top:18px;font-family:monospace;font-size:13px;color:#94a3b8;word-break:break-all}
        .foot{margin-top:22px;font-size:13px;color:#94a3b8}
      </style></head><body><div class="poster">
        <div class="header">
          ${logo ? `<img class="logo" src="${logo}" alt="logo"/>` : ""}
          <div class="name">${company.name}</div>
          ${acct ? `<div class="acct">Account #: ${acct}</div>` : ""}
        </div>
        <h1>${heading}</h1>
        <div class="qr"><img src="${img}" alt="QR code"/></div>
        <p>${sub}</p>
        <div class="url">${qrModal.url}</div>
        <div class="foot">${company.name} · Powered by RightPay</div>
      </div>
      <script>window.onload=function(){setTimeout(function(){window.print()},350)}<\/script>
      </body></html>`);
    w.document.close();
  };
  const inputCls = "w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition";
  const [form, setForm] = useState({
    name: company.name, primary_color: company.primary_color, currency: company.currency,
    account_number: company.account_number || "",
    approvers: (company.approvers || []).map((a) => (typeof a === "string" ? { email: a, required: false } : { email: a.email || "", required: !!a.required })),
    geofence_lat: company.geofence_lat ?? "", geofence_lng: company.geofence_lng ?? "",
    geofence_radius: company.geofence_radius ?? 150, require_geofence: company.require_geofence,
    require_selfie: company.require_selfie, overtime_threshold: company.overtime_threshold,
    overtime_multiplier: company.overtime_multiplier,
    overtime_weekly_threshold: company.overtime_weekly_threshold ?? 40,
    require_visitor_photo: company.require_visitor_photo || false,
    host_options: (company.host_options || []).join(", "),
    badge_label: company.badge_label || "dymo_30857",
    kiosk_mode: company.kiosk_mode || "both",
    visit_types: (company.visit_types || []).join(", "),
    employee_button_color: company.employee_button_color || "#1d54c4",
    visitor_button_color: company.visitor_button_color || "#0891b2",
    distance_unit: company.distance_unit || "meters",
    payroll_processor_email: company.payroll_processor_email || "",
    badge_printing_enabled: company.badge_printing_enabled !== false,
    webcam_enabled: company.webcam_enabled !== false,
    kiosk_exit_pin: "",
    kiosk_bg_color: company.kiosk_bg_color || "#020617",
  });
  const [logoData, setLogoData] = useState(null);
  const [saving, setSaving] = useState(false);
  const origin = window.location.origin;

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoData(reader.result);
    reader.readAsDataURL(file);
  };

  const useMyLocation = () => {
    navigator.geolocation?.getCurrentPosition((pos) => {
      setForm((f) => ({ ...f, geofence_lat: pos.coords.latitude.toFixed(6), geofence_lng: pos.coords.longitude.toFixed(6) }));
      toast.success("Location set to current position");
    }, () => toast.error("Could not get location"));
  };

  const removeLock = async () => {
    try { await api.put("/company", { kiosk_exit_pin: "" }); toast.success("Kiosk exit lock removed"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const downloadManual = async () => {
    try {
      const { data } = await api.get("/company/manual", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "user-manual.pdf"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the manual"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, primary_color: form.primary_color, currency: form.currency,
        account_number: form.account_number,
        approvers: form.approvers.filter((a) => a.email.trim()).map((a) => ({ email: a.email.trim().toLowerCase(), required: !!a.required })),
        geofence_lat: form.geofence_lat === "" ? null : parseFloat(form.geofence_lat),
        geofence_lng: form.geofence_lng === "" ? null : parseFloat(form.geofence_lng),
        geofence_radius: parseInt(form.geofence_radius) || 150,
        require_geofence: form.require_geofence, require_selfie: form.require_selfie,
        webcam_enabled: form.webcam_enabled,
        overtime_threshold: parseFloat(form.overtime_threshold), overtime_multiplier: parseFloat(form.overtime_multiplier),
        overtime_weekly_threshold: parseFloat(form.overtime_weekly_threshold) || 40,
        require_visitor_photo: form.require_visitor_photo,
        host_options: form.host_options.split(",").map((s) => s.trim()).filter(Boolean),
        badge_label: form.badge_label,
        kiosk_mode: form.kiosk_mode,
        visit_types: form.visit_types.split(",").map((s) => s.trim()).filter(Boolean),
        employee_button_color: form.employee_button_color,
        visitor_button_color: form.visitor_button_color,
        distance_unit: form.distance_unit,
        payroll_processor_email: form.payroll_processor_email.trim() || null,
        badge_printing_enabled: form.badge_printing_enabled,
        kiosk_bg_color: form.kiosk_bg_color,
      };
      if (form.kiosk_exit_pin.trim()) payload.kiosk_exit_pin = form.kiosk_exit_pin.trim();
      if (logoData) payload.logo_data = logoData;
      await api.put("/company", payload);
      toast.success("Settings saved");
      setLogoData(null);
      reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Settings</h1><p className="text-slate-400 text-sm mt-1">Branding, geofence, payroll & access</p></div>
        <button data-testid="save-settings-button" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-full px-6 py-2.5 font-semibold disabled:opacity-60 transition-colors">{saving ? "Saving…" : "Save changes"}</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="settings-subtabs">
        {SETTINGS_SUBTABS.map((s) => (
          <button key={s.key} data-testid={`settings-subtab-${s.key}`} onClick={() => setSub(s.key)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${sub === s.key ? "bg-blue-600 text-white" : "bg-slate-900 border border-slate-800 text-slate-400 hover:text-slate-100 hover:bg-slate-800"}`}>
            <s.icon className="w-4 h-4" /> {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SettingsSection icon={Monitor} title="Kiosk & mobile mode" group="clock" active={sub}>
          <div className="space-y-4">
            <Field label="What can people do at the kiosk / mobile screen?">
              <select data-testid="kiosk-mode-select" value={form.kiosk_mode} onChange={(e) => setForm((f) => ({ ...f, kiosk_mode: e.target.value }))} className={inputCls}>
                <option value="both">Time clock + Visitors (two big buttons on kiosk)</option>
                <option value="clock">Time clock only</option>
                <option value="visitors">Visitors only</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Employee button color">
                <div className="flex items-center gap-2">
                  <input data-testid="employee-button-color" type="color" value={form.employee_button_color} onChange={(e) => setForm((f) => ({ ...f, employee_button_color: e.target.value }))} className="w-12 h-11 rounded-lg bg-slate-950 border border-slate-700 cursor-pointer" />
                  <input value={form.employee_button_color} onChange={(e) => setForm((f) => ({ ...f, employee_button_color: e.target.value }))} className={inputCls + " font-mono"} />
                </div>
              </Field>
              <Field label="Visitor button color">
                <div className="flex items-center gap-2">
                  <input data-testid="visitor-button-color" type="color" value={form.visitor_button_color} onChange={(e) => setForm((f) => ({ ...f, visitor_button_color: e.target.value }))} className="w-12 h-11 rounded-lg bg-slate-950 border border-slate-700 cursor-pointer" />
                  <input value={form.visitor_button_color} onChange={(e) => setForm((f) => ({ ...f, visitor_button_color: e.target.value }))} className={inputCls + " font-mono"} />
                </div>
              </Field>
            </div>
            <Field label="Visitor visit types (comma-separated buttons at sign-in)">
              <input data-testid="visit-types-input" value={form.visit_types} onChange={(e) => setForm((f) => ({ ...f, visit_types: e.target.value }))} placeholder="Guest, Delivery, Interview, Contractor" className={inputCls} />
            </Field>
            <Field label="Kiosk exit lock PIN">
              <input data-testid="kiosk-exit-pin-input" inputMode="numeric" value={form.kiosk_exit_pin} onChange={(e) => setForm((f) => ({ ...f, kiosk_exit_pin: e.target.value.replace(/\D/g, "") }))}
                placeholder={company.kiosk_exit_lock ? "Lock is ON — type a new PIN to change" : "Set a PIN to lock the kiosk exit"} className={inputCls + " font-mono tracking-widest"} />
              <p className="text-xs text-slate-500 mt-1.5">Prevents staff from accidentally leaving the kiosk on tablets. Leaving is blocked until this PIN is entered.
                {company.kiosk_exit_lock && <button type="button" data-testid="remove-kiosk-lock" onClick={removeLock} className="ml-2 text-rose-400 hover:text-rose-300 underline">Remove lock</button>}
              </p>
            </Field>
            <Field label="Kiosk background color">
              <div className="flex items-center gap-2">
                <input data-testid="kiosk-bg-color" type="color" value={form.kiosk_bg_color} onChange={(e) => setForm((f) => ({ ...f, kiosk_bg_color: e.target.value }))} className="w-12 h-11 rounded-lg bg-slate-950 border border-slate-700 cursor-pointer" />
                <input data-testid="kiosk-bg-color-hex" value={form.kiosk_bg_color} onChange={(e) => setForm((f) => ({ ...f, kiosk_bg_color: e.target.value }))} className={inputCls + " font-mono"} />
              </div>
            </Field>
          </div>
        </SettingsSection>

        <SettingsSection icon={Palette} title="Branding" group="branding" active={sub}>
          <div className="space-y-4">
            <Field label="Company name"><input data-testid="company-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} /></Field>
            <Field label="Account number"><input data-testid="company-account-number-input" value={form.account_number} onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} placeholder="e.g. ACC-100234" className={inputCls} /></Field>
            <Field label="Brand color">
              <div className="flex items-center gap-3">
                <input data-testid="company-branding-color-picker" type="color" value={form.primary_color} onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))} className="w-12 h-11 rounded-lg bg-slate-950 border border-slate-700 cursor-pointer" />
                <input value={form.primary_color} onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))} className={inputCls + " font-mono"} />
              </div>
            </Field>
            <Field label="Logo">
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-xl p-2 w-24 h-16 flex items-center justify-center overflow-hidden">
                  <img src={logoData || fileUrl(company.logo_path) || "/rightpay-logo.png"} alt="logo" className="max-h-full max-w-full object-contain" />
                </div>
                <label className="cursor-pointer bg-slate-800 hover:bg-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors">
                  Upload logo
                  <input data-testid="company-logo-upload-input" type="file" accept="image/*" onChange={onLogo} className="hidden" />
                </label>
              </div>
            </Field>
            <Field label="Currency">
              <select data-testid="company-currency-select" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className={inputCls}>
                {["USD", "EUR", "GBP", "CAD", "AUD", "INR"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
        </SettingsSection>

        <SettingsSection icon={MapPin} title="Geofence & clock rules" group="clock" active={sub}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude"><input data-testid="geofence-lat-input" value={form.geofence_lat} onChange={(e) => setForm((f) => ({ ...f, geofence_lat: e.target.value }))} className={inputCls + " font-mono"} /></Field>
              <Field label="Longitude"><input data-testid="geofence-lng-input" value={form.geofence_lng} onChange={(e) => setForm((f) => ({ ...f, geofence_lng: e.target.value }))} className={inputCls + " font-mono"} /></Field>
            </div>
            <button onClick={useMyLocation} data-testid="use-my-location-button" className="text-sm text-blue-400 hover:text-blue-300 inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Use my current location</button>
            <Field label="Distance unit">
              <select data-testid="distance-unit-select" value={form.distance_unit} onChange={(e) => setForm((f) => ({ ...f, distance_unit: e.target.value }))} className={inputCls}>
                <option value="feet">Feet</option>
                <option value="miles">Miles</option>
                <option value="meters">Meters</option>
                <option value="kilometers">Kilometers</option>
              </select>
            </Field>
            <Field label={`Radius (${form.geofence_radius} ${UNIT_ABBR[form.distance_unit] || "m"})`}>
              <input data-testid="geofence-radius-input" type="range" min="20" max="1000" step="10" value={form.geofence_radius} onChange={(e) => setForm((f) => ({ ...f, geofence_radius: e.target.value }))} className="w-full accent-blue-600" />
            </Field>
            <div className="flex items-center justify-between"><span className="text-sm">Enforce geofence on mobile</span><Toggle testid="require-geofence-toggle" on={form.require_geofence} onClick={() => setForm((f) => ({ ...f, require_geofence: !f.require_geofence }))} /></div>
          </div>
        </SettingsSection>

        <SettingsSection icon={Camera} title="Webcam" group="clock" active={sub}>
          <p className="text-xs text-slate-500 mb-4">Turn the webcam on to capture a photo when people clock in or check in. Turn it off to skip the camera entirely.</p>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl bg-slate-950/40 border border-slate-800 px-4 py-3">
              <div>
                <div className="text-sm font-semibold">Webcam master switch</div>
                <div className="text-xs text-slate-500">Off disables the camera everywhere, overriding the toggles below.</div>
              </div>
              <Toggle testid="webcam-enabled-toggle" on={form.webcam_enabled} onClick={() => setForm((f) => ({ ...f, webcam_enabled: !f.webcam_enabled }))} />
            </div>
            <div className={`space-y-4 ${form.webcam_enabled ? "" : "opacity-40 pointer-events-none select-none"}`} data-testid="webcam-audience-toggles">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Employee webcam</div>
                  <div className="text-xs text-slate-500">Capture a photo each time an employee clocks in/out.</div>
                </div>
                <Toggle testid="require-selfie-toggle" on={form.require_selfie} onClick={() => setForm((f) => ({ ...f, require_selfie: !f.require_selfie }))} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Visitor webcam</div>
                  <div className="text-xs text-slate-500">Capture a photo when a visitor checks in.</div>
                </div>
                <Toggle testid="require-visitor-photo-toggle" on={form.require_visitor_photo} onClick={() => setForm((f) => ({ ...f, require_visitor_photo: !f.require_visitor_photo }))} />
              </div>
            </div>
          </div>
        </SettingsSection>

        <SettingsSection icon={UserPlus} title="Visitor host options" group="visitors" active={sub}>
          <Field label="Employees appear automatically. Add departments or organizations (comma-separated) for the visitor sign-in dropdown.">
            <textarea data-testid="host-options-input" rows={3} value={form.host_options} onChange={(e) => setForm((f) => ({ ...f, host_options: e.target.value }))} placeholder="Front Desk, HR Department, Warehouse" className={inputCls + " resize-none"} />
          </Field>
        </SettingsSection>

        <SettingsSection icon={Printer} title="Visitor badge printing" group="visitors" active={sub}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-medium">Enable badge printing</div>
              <div className="text-xs text-slate-500">Turn off to check visitors in without printing a badge.</div>
            </div>
            <Toggle testid="badge-printing-toggle" on={form.badge_printing_enabled} onClick={() => setForm((f) => ({ ...f, badge_printing_enabled: !f.badge_printing_enabled }))} />
          </div>
          <Field label="Label size (DYMO LabelWriter 450/550 & Brother QL-820NWB supported)">
            <select data-testid="badge-label-select" value={form.badge_label} onChange={(e) => setForm((f) => ({ ...f, badge_label: e.target.value }))} className={inputCls}>
              {Object.entries(BADGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
            </select>
          </Field>
          <p className="text-xs text-slate-500 mt-2">When printing a badge, choose your DYMO or Brother QL printer in the browser dialog. The page size is set to the selected label so it feeds correctly.</p>
        </SettingsSection>

        <SettingsSection icon={DollarSign} title="Overtime rules" group="payroll" active={sub}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Daily OT after (hrs)"><input data-testid="ot-threshold-input" type="number" step="0.5" value={form.overtime_threshold} onChange={(e) => setForm((f) => ({ ...f, overtime_threshold: e.target.value }))} className={inputCls + " font-mono"} /></Field>
            <Field label="OT multiplier"><input data-testid="ot-multiplier-input" type="number" step="0.1" value={form.overtime_multiplier} onChange={(e) => setForm((f) => ({ ...f, overtime_multiplier: e.target.value }))} className={inputCls + " font-mono"} /></Field>
            <Field label="Weekly OT after (hrs)"><input data-testid="ot-weekly-input" type="number" step="1" value={form.overtime_weekly_threshold} onChange={(e) => setForm((f) => ({ ...f, overtime_weekly_threshold: e.target.value }))} className={inputCls + " font-mono"} /></Field>
          </div>
          <p className="text-xs text-slate-500 mt-2">Weekly OT drives the overtime alerts on the Schedules calendar.</p>
        </SettingsSection>

        <SettingsSection icon={Palette} title="Appearance" group="branding" active={sub}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Theme</div>
              <div className="text-xs text-slate-500">Switch between light and dark mode. Your choice is saved on this device.</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-400 capitalize">{theme}</span>
              <Toggle testid="theme-mode-toggle" on={theme === "light"} onClick={toggle} />
            </div>
          </div>
        </SettingsSection>

        <SettingsSection icon={FileSpreadsheet} title="Help &amp; user manual" group="links" active={sub}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Download the user manual</div>
              <div className="text-xs text-slate-500">A branded PDF guide covering the kiosk, mobile clock, and admin dashboard for your team.</div>
            </div>
            <button data-testid="download-manual-button" onClick={downloadManual} className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 rounded-full px-5 py-2.5 font-semibold transition-colors shrink-0">
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </SettingsSection>

        <SettingsSection icon={Send} title="Payroll delivery" group="payroll" active={sub}>
          <Field label="Payroll processor email">
            <input data-testid="processor-email-input" type="email" value={form.payroll_processor_email} onChange={(e) => setForm((f) => ({ ...f, payroll_processor_email: e.target.value }))} placeholder="payroll@bureau.com" className={inputCls} />
          </Field>
          <p className="text-xs text-slate-500 mt-2">Once all timesheets are approved, the <b>Send to Processor</b> button on the Approvals tab emails the payroll CSV here — in your connected provider's format if one is set up, otherwise a standard CSV.</p>
          <div className="mt-4 pt-4 border-t border-slate-800">
            <Field label="Saved approvers. Mark an approver as Required to demand their sign-off before a schedule or payroll auto-publishes; Optional approvers are notified but don't block.">
              <div className="space-y-2" data-testid="approvers-editor">
                {form.approvers.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input data-testid={`approver-email-${i}`} type="email" value={a.email}
                      onChange={(e) => setForm((f) => ({ ...f, approvers: f.approvers.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x) }))}
                      placeholder="approver@company.com" className={inputCls + " flex-1"} />
                    <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer whitespace-nowrap px-2">
                      <input data-testid={`approver-required-${i}`} type="checkbox" checked={a.required}
                        onChange={(e) => setForm((f) => ({ ...f, approvers: f.approvers.map((x, idx) => idx === i ? { ...x, required: e.target.checked } : x) }))}
                        className="w-4 h-4 accent-blue-600" /> Required
                    </label>
                    <button data-testid={`approver-remove-${i}`} onClick={() => setForm((f) => ({ ...f, approvers: f.approvers.filter((_, idx) => idx !== i) }))}
                      className="p-2 text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button data-testid="add-approver-button" onClick={() => setForm((f) => ({ ...f, approvers: [...f.approvers, { email: "", required: false }] }))}
                  className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add approver</button>
              </div>
            </Field>
          </div>
        </SettingsSection>

        <SettingsSection icon={KeyRound} title="Clock URLs & mobile app" group="links" active={sub}>
          <div className="space-y-3 text-sm">
            <p className="text-slate-400 text-xs">Share these links with staff. On a phone, open the mobile link and choose <b>Add to Home Screen</b> to install the RightPay app (works offline).</p>
            <div className="flex items-center gap-2">
              <Monitor className="w-4 h-4 text-slate-500 shrink-0" />
              <code className="flex-1 truncate bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-2 font-mono text-xs text-slate-300">{origin}/kiosk/{company.id}</code>
              <button data-testid="qr-kiosk-url" onClick={() => setQrModal({ url: `${origin}/kiosk/${company.id}`, label: "Kiosk QR code", kind: "kiosk" })} className="p-2 text-slate-400 hover:text-blue-400"><QrCode className="w-4 h-4" /></button>
              <button data-testid="copy-kiosk-url" onClick={() => copy(`${origin}/kiosk/${company.id}`, "Kiosk URL copied")} className="p-2 text-slate-400 hover:text-blue-400"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-slate-500 shrink-0" />
              <code className="flex-1 truncate bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-2 font-mono text-xs text-slate-300">{origin}/mobile/{company.id}</code>
              <button data-testid="qr-mobile-url" onClick={() => setQrModal({ url: `${origin}/mobile/${company.id}`, label: "Mobile app QR code", kind: "mobile" })} className="p-2 text-slate-400 hover:text-blue-400"><QrCode className="w-4 h-4" /></button>
              <button data-testid="copy-mobile-url" onClick={() => copy(`${origin}/mobile/${company.id}`, "Mobile URL copied")} className="p-2 text-slate-400 hover:text-blue-400"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-slate-500 shrink-0" />
              <code className="flex-1 truncate bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-2 font-mono text-xs text-slate-300">{origin}/portal/{company.id}</code>
              <button data-testid="qr-portal-url" onClick={() => setQrModal({ url: `${origin}/portal/${company.id}`, label: "Employee portal QR code", kind: "portal" })} className="p-2 text-slate-400 hover:text-blue-400"><QrCode className="w-4 h-4" /></button>
              <button data-testid="copy-portal-url" onClick={() => copy(`${origin}/portal/${company.id}`, "Employee portal URL copied")} className="p-2 text-slate-400 hover:text-blue-400"><Copy className="w-4 h-4" /></button>
            </div>
            <p className="text-slate-500 text-xs -mt-1">The portal lets staff view their published schedule &amp; swap history with just their PIN.</p>
            <p className="text-slate-500 text-xs">Tap the <QrCode className="w-3 h-3 inline -mt-0.5" /> icon on any link to show a scannable QR code you can print or share.</p>
            <div className="pt-3 border-t border-slate-800">
              <p className="text-slate-400 text-xs mb-1">Developer API base</p>
              <code className="block truncate bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-2 font-mono text-xs text-slate-300">{API}/v1/  ·  header: X-API-Key</code>
            </div>
          </div>
        </SettingsSection>

        <Dialog open={!!qrModal} onOpenChange={(o) => { if (!o) setQrModal(null); }}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-xs" data-testid="qr-modal">
            <DialogHeader><DialogTitle>{qrModal?.label}</DialogTitle></DialogHeader>
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="bg-white p-3 rounded-xl">
                {qrModal && <QRCodeCanvas id="qr-canvas" value={qrModal.url} size={220} level="M" marginSize={2} />}
              </div>
              <code className="text-[11px] text-slate-400 break-all text-center">{qrModal?.url}</code>
              <div className="grid grid-cols-2 gap-2 w-full">
                <button data-testid="qr-download-button" onClick={downloadQr} className="rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 font-semibold transition-colors inline-flex items-center justify-center gap-2"><Download className="w-4 h-4" /> PNG</button>
                <button data-testid="qr-print-poster-button" onClick={printPoster} className="rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold transition-colors inline-flex items-center justify-center gap-2"><Printer className="w-4 h-4" /> Print poster</button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}


function Visitors({ company }) {
  const [visitors, setVisitors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", organization: "", host: "", purpose: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [hostOptions, setHostOptions] = useState({ employees: [], organizations: [] });
  const [viewV, setViewV] = useState(null);

  const load = useCallback(() => api.get("/visitors").then(({ data }) => setVisitors(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/public/companies/${company.id}/hosts`).then(({ data }) => setHostOptions(data)).catch(() => {}); }, [company.id]);

  const checkin = async (print) => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/visitors", form);
      toast.success(`${data.name} checked in`);
      if (print) printBadge(data, company);
      setOpen(false); setForm({ name: "", organization: "", host: "", purpose: "", phone: "" }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const checkout = async (id) => { await api.post(`/visitors/${id}/checkout`); toast.success("Checked out"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete visitor record?")) return; await api.delete(`/visitors/${id}`); load(); };

  const active = visitors.filter((v) => v.status === "checked_in");
  const inputCls = "mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Visitors</h1><p className="text-slate-400 text-sm mt-1">{active.length} currently on-site · {visitors.length} total</p></div>
        <button data-testid="visitor-checkin-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 rounded-full px-5 py-2.5 font-semibold transition-colors">
          <UserPlus className="w-4 h-4" /> Check in visitor
        </button>
      </div>

      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="visitors-table">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">Badge</th><th className="text-left px-5 py-3">Visitor</th><th className="text-left px-5 py-3 hidden sm:table-cell">Host</th><th className="text-left px-5 py-3">In</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {visitors.map((v) => (
              <tr key={v.id} className="border-t border-slate-800" data-testid={`visitor-row-${v.id}`}>
                <td className="px-5 py-3 font-mono text-blue-400">{v.badge_number}</td>
                <td className="px-5 py-3"><div className="font-medium">{v.name}</div>{v.organization && <div className="text-xs text-slate-500">{v.organization}</div>}</td>
                <td className="px-5 py-3 text-slate-400 hidden sm:table-cell">{v.host || "—"}</td>
                <td className="px-5 py-3 font-mono text-slate-300">{fmtDateTime(v.check_in)}</td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${v.status === "checked_in" ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-800 text-slate-500"}`}>{v.status === "checked_in" ? "on-site" : "left"}</span>
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button data-testid={`view-visitor-${v.id}`} onClick={() => setViewV(v)} className="p-2 text-slate-400 hover:text-slate-100 transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                  {company.badge_printing_enabled !== false && <button data-testid={`print-badge-${v.id}`} onClick={() => printBadge(v, company)} className="p-2 text-slate-400 hover:text-blue-400 transition-colors" title="Print badge"><Printer className="w-4 h-4" /></button>}
                  {v.status === "checked_in" && <button data-testid={`checkout-visitor-${v.id}`} onClick={() => checkout(v.id)} className="p-2 text-slate-400 hover:text-amber-400 transition-colors" title="Check out"><LogInIcon className="w-4 h-4 rotate-180" /></button>}
                  <button onClick={() => remove(v.id)} className="p-2 text-slate-400 hover:text-rose-400 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {visitors.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No visitors yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle>Check in visitor</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-slate-300">Full name *</label>
              <input data-testid="visitor-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Organization</label>
              <input data-testid="visitor-organization-input" value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Host / who they're visiting</label>
              <select data-testid="visitor-host-input" value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))} className={inputCls}>
                <option value="">Select host…</option>
                {hostOptions.employees.length > 0 && <optgroup label="Employees">{hostOptions.employees.map((h) => <option key={h.name} value={h.name}>{h.name}{h.title ? ` — ${h.title}` : ""}{h.organization ? ` (${h.organization})` : ""}</option>)}</optgroup>}
                {hostOptions.organizations.length > 0 && <optgroup label="Departments / Organizations">{hostOptions.organizations.map((h) => <option key={h} value={h}>{h}</option>)}</optgroup>}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Reason for visit</label>
              <textarea data-testid="visitor-purpose-input" rows={2} value={form.purpose} onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))} className={inputCls + " resize-none"} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Phone</label>
              <input data-testid="visitor-phone-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <button data-testid="visitor-checkin-only" onClick={() => checkin(false)} disabled={saving} className="flex-1 rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 font-semibold transition-colors">Check in</button>
            {company.badge_printing_enabled !== false && <button data-testid="visitor-checkin-print" onClick={() => checkin(true)} disabled={saving} className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold inline-flex items-center justify-center gap-2 transition-colors"><Printer className="w-4 h-4" /> Check in & print badge</button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewV} onOpenChange={(o) => !o && setViewV(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="visitor-view-modal">
          <DialogHeader><DialogTitle>Visitor details</DialogTitle></DialogHeader>
          {viewV && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-4">
                {viewV.photo_path ? <img src={fileUrl(viewV.photo_path)} alt={viewV.name} className="w-16 h-16 rounded-xl object-cover border border-slate-700" /> : <div className="w-16 h-16 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-xl font-bold">{viewV.name[0]}</div>}
                <div><div className="text-lg font-bold">{viewV.name}</div>{viewV.organization && <div className="text-sm text-slate-400">{viewV.organization}</div>}<div className="font-mono text-xs text-blue-400 mt-0.5">{viewV.badge_number}</div></div>
              </div>
              <div className="text-sm"><span className="text-slate-400">Host: </span>{viewV.host || "—"}</div>
              <div className="text-sm"><span className="text-slate-400">Visit type: </span>{viewV.visit_type || "—"}</div>
              <div className="text-sm"><span className="text-slate-400">Reason: </span>{viewV.purpose || "—"}</div>
              <div className="text-sm"><span className="text-slate-400">Phone: </span>{viewV.phone || "—"}</div>
              <div className="text-sm"><span className="text-slate-400">Checked in: </span>{fmtDateTime(viewV.check_in)}</div>
              <div className="text-sm"><span className="text-slate-400">Status: </span>{viewV.status === "checked_in" ? "On-site" : "Checked out"}</div>
            </div>
          )}
          <DialogFooter>
            {company.badge_printing_enabled !== false && <button data-testid="visitor-view-print" onClick={() => printBadge(viewV, company)} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold inline-flex items-center justify-center gap-2 transition-colors"><Printer className="w-4 h-4" /> Print badge</button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApprovalsInbox({ company, onChange }) {
  const [requests, setRequests] = useState([]);
  const [typeF, setTypeF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [q, setQ] = useState("");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const [resending, setResending] = useState(null);

  const load = useCallback(() => {
    api.get("/approvals/requests").then(({ data }) => { setRequests(data.requests || []); onChange?.(); }).catch(() => {});
  }, [onChange]);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    setResending(id);
    try {
      const { data } = await api.post(`/approvals/requests/${id}/resend?base_url=${encodeURIComponent(window.location.origin)}`);
      toast.success(data.emailed ? "Reminder re-sent to the approver" : "Reminder created (email not configured)");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed to resend"); }
    finally { setResending(null); }
  };

  const filtered = requests.filter((r) => {
    if (typeF !== "all" && r.type !== typeF) return false;
    if (statusF !== "all" && r.status !== statusF) return false;
    if (q.trim()) {
      const hay = `${r.approver_email || ""} ${r.requested_by_name || ""} ${r.type || ""} ${r.period_start || ""} ${r.period_end || ""} ${r.note || ""}`.toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    const created = (r.created_at || "").slice(0, 10);
    if (dFrom && created < dFrom) return false;
    if (dTo && created > dTo) return false;
    return true;
  });
  const hasFilters = q.trim() || dFrom || dTo || typeF !== "all" || statusF !== "all";
  const clearFilters = () => { setQ(""); setDFrom(""); setDTo(""); setTypeF("all"); setStatusF("all"); };
  const pending = requests.filter((r) => r.status === "pending");
  const SB = { pending: "bg-amber-500/15 text-amber-300 border-amber-500/30", approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", declined: "bg-rose-500/15 text-rose-300 border-rose-500/30" };

  return (
    <div data-testid="approvals-inbox">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Approval requests</h1>
          <p className="text-slate-400 text-sm mt-1">{pending.length} awaiting response · {requests.length} total{company?.name ? ` · ${company.name}` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input data-testid="approvals-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search approver, requester, note…"
              className="rounded-xl bg-slate-900 border border-slate-800 pl-9 pr-3 py-2 text-sm outline-none focus:border-blue-500 w-full sm:w-64" />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-400">From
            <input data-testid="approvals-date-from" type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} className="rounded-xl bg-slate-900 border border-slate-800 px-2.5 py-2 text-sm outline-none focus:border-blue-500" />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-400">To
            <input data-testid="approvals-date-to" type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} className="rounded-xl bg-slate-900 border border-slate-800 px-2.5 py-2 text-sm outline-none focus:border-blue-500" />
          </label>
          <select data-testid="approvals-filter-type" value={typeF} onChange={(e) => setTypeF(e.target.value)} className="rounded-xl bg-slate-900 border border-slate-800 px-3 py-2 text-sm outline-none focus:border-blue-500">
            <option value="all">All types</option><option value="schedule">Schedule</option><option value="payroll">Payroll</option>
          </select>
          <select data-testid="approvals-filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className="rounded-xl bg-slate-900 border border-slate-800 px-3 py-2 text-sm outline-none focus:border-blue-500">
            <option value="all">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="declined">Declined</option>
          </select>
          {hasFilters && <button data-testid="approvals-clear-filters" onClick={clearFilters} className="rounded-xl bg-slate-800 hover:bg-slate-700 px-3 py-2 text-sm font-medium transition-colors">Clear</button>}
        </div>
      </div>

      {pending.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-4" data-testid="approvals-pending-banner">
          <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm mb-3"><Clock className="w-4 h-4" /> Awaiting response</div>
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-slate-300"><b className="capitalize">{r.type}</b> · {fmtDateStr(r.period_start)} → {fmtDateStr(r.period_end)} · {r.approver_email}{r.batch_id && <span className={`ml-2 text-[10px] font-semibold ${r.required ? "text-blue-300" : "text-slate-500"}`}>{r.required ? "· Required" : "· Optional"}</span>}</span>
                <button data-testid={`resend-pending-${r.id}`} disabled={resending === r.id} onClick={() => resend(r.id)} className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 hover:bg-slate-700 px-3 py-1.5 text-xs font-semibold disabled:opacity-60 transition-colors"><RefreshCw className="w-3.5 h-3.5" /> {resending === r.id ? "Sending…" : "Resend"}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Period</th><th className="text-left px-5 py-3">Approver</th><th className="text-left px-5 py-3">Requested by</th><th className="text-left px-5 py-3">Created</th><th className="text-left px-5 py-3">Status</th><th /></tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-800 align-top" data-testid={`approval-inbox-row-${r.id}`}>
                <td className="px-5 py-3 capitalize font-medium">{r.type}</td>
                <td className="px-5 py-3 font-mono text-slate-300 whitespace-nowrap">{fmtDateStr(r.period_start)} → {fmtDateStr(r.period_end)}</td>
                <td className="px-5 py-3 text-slate-300">
                  {r.approver_email}
                  {r.batch_id && <span data-testid={`approver-role-${r.id}`} className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${r.required ? "bg-blue-500/15 text-blue-300 border border-blue-500/30" : "bg-slate-700/50 text-slate-400 border border-slate-600"}`}>{r.required ? "Required" : "Optional"}</span>}
                </td>
                <td className="px-5 py-3 text-slate-400">{r.requested_by_name}</td>
                <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs capitalize ${SB[r.status] || ""}`}>{r.status}</span>
                  {r.note ? <div className="text-xs text-slate-500 mt-1 max-w-[220px] truncate" title={r.note}>“{r.note}”</div> : null}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">{r.status === "pending" && <button data-testid={`inbox-resend-${r.id}`} disabled={resending === r.id} onClick={() => resend(r.id)} className="text-blue-400 hover:text-blue-300 text-xs font-semibold disabled:opacity-60">Resend</button>}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500" data-testid="approvals-inbox-empty">No approval requests match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Approvals({ currency }) {
  const [df, dt] = useDefaultRange();
  const [from, setFrom] = useState(df);
  const [to, setTo] = useState(dt);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/approvals?from=${from}&to=${to}`).then(({ data }) => setData(data)).catch(() => {}), [from, to]);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => {
    try { await api.post(`/approvals/${id}/approve?from=${from}&to=${to}`); toast.success("Approved for payroll"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resolveC = async (cid) => { await api.post(`/corrections/${cid}/resolve`); toast.success("Resolved"); load(); };
  const sendToProcessor = async () => {
    try {
      const { data } = await api.post(`/payroll/send-to-processor?from=${from}&to=${to}`);
      toast.success(`Payroll sent to ${data.recipient} (${data.format})`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed to send"); }
  };
  const [rpUploading, setRpUploading] = useState(false);
  const uploadToRightPay = async () => {
    setRpUploading(true);
    try {
      const { data } = await api.post(`/payroll/upload-to-rightpay?from=${from}&to=${to}`);
      toast.success(`Uploaded ${data.employees} employee(s) & ${data.punches} punch(es) to RightPay`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload to RightPay failed"); }
    finally { setRpUploading(false); }
  };
  const exportCsv = async () => {
    const res = await api.get(`/payroll/export?from=${from}&to=${to}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `payroll_${from}_${to}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const rows = data?.rows || [];
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl font-bold">Timesheet approvals</h1><p className="text-slate-400 text-sm mt-1">Employees approve their hours, then you approve for payroll</p></div>
        <div className="flex items-center gap-3">
          <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
          {data?.all_approved && (
            <button data-testid="send-to-processor-button" onClick={sendToProcessor}
              className="animate-flash inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 rounded-full px-5 py-2.5 font-semibold text-white transition-colors">
              <Send className="w-4 h-4" /> Send to Processor
            </button>
          )}
          {data?.all_approved && (
            <button data-testid="upload-rightpay-button" onClick={uploadToRightPay} disabled={rpUploading}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 rounded-full px-5 py-2.5 font-semibold text-white transition-colors">
              <UploadCloud className="w-4 h-4" /> {rpUploading ? "Uploading…" : "Upload to RightPay"}
            </button>
          )}
          <button data-testid="approvals-export-button" onClick={exportCsv} disabled={!data?.all_approved} title={data?.all_approved ? "" : "Approve all employees first"}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-full px-5 py-2.5 font-semibold transition-colors">
            <Download className="w-4 h-4" /> Export for payroll
          </button>
        </div>
      </div>
      {data?.all_approved && <div className="mb-4 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3" data-testid="all-approved-banner">✓ All employees approved — timesheet is ready for print, export or submission to your payroll bureau.</div>}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="approvals-table">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">Employee</th><th className="text-left px-5 py-3">Hours</th><th className="text-left px-5 py-3">Gross</th><th className="text-left px-5 py-3">Employee</th><th className="text-left px-5 py-3">Payroll</th><th className="text-right px-5 py-3">Action</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.employee_id} className="border-t border-slate-800" data-testid={`approval-row-${r.employee_id}`}>
                <td className="px-5 py-3 font-medium">{r.employee_name}
                  {r.corrections.length > 0 && r.corrections.map((c) => (
                    <div key={c.id} className="mt-1 text-xs text-amber-300 flex items-center gap-2">⚠ {c.message} <button onClick={() => resolveC(c.id)} className="text-blue-400 hover:text-blue-300 underline">resolve</button></div>
                  ))}
                </td>
                <td className="px-5 py-3 font-mono">{r.total_hours}h</td>
                <td className="px-5 py-3 font-mono text-emerald-400">{money(r.gross_pay, currency)}</td>
                <td className="px-5 py-3">{r.employee_approved ? <span className="text-emerald-400 text-xs">✓ approved</span> : <span className="text-slate-500 text-xs">pending</span>}</td>
                <td className="px-5 py-3">{r.admin_approved ? <span className="text-emerald-400 text-xs">✓ approved</span> : <span className="text-slate-500 text-xs">—</span>}</td>
                <td className="px-5 py-3 text-right">
                  {!r.admin_approved && <button data-testid={`admin-approve-${r.employee_id}`} onClick={() => approve(r.employee_id)} disabled={!r.employee_approved} title={r.employee_approved ? "" : "Waiting on employee approval"}
                    className="rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 px-3 py-1.5 text-xs font-semibold transition-colors">Approve for payroll</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No employees with hours in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PROVIDERS = [
  { key: "quickbooks", name: "QuickBooks", color: "#2ca01c", blurb: "Sync hours & pay into QuickBooks payroll.", fields: [["realm_id", "Company / Realm ID"], ["access_token", "Access Token"]] },
  { key: "adp", name: "ADP", color: "#d0271d", blurb: "Push paydata batches to ADP Workforce Now.", fields: [["client_id", "Client ID"], ["client_secret", "Client Secret"], ["company_code", "Company Code"]] },
  { key: "paychex", name: "Paychex", color: "#0071ce", blurb: "Deliver time & earnings to Paychex Flex.", fields: [["client_id", "Client ID"], ["client_secret", "Client Secret"], ["client_company_id", "Company ID"]] },
];

function Integrations({ company }) {
  const [status, setStatus] = useState({});
  const [dialog, setDialog] = useState(null); // provider key
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/integrations").then(({ data }) => setStatus(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try { await api.post(`/integrations/${dialog}/connect`, form); toast.success(`${dialog} connected`); setDialog(null); setForm({}); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const disconnect = async (p) => { await api.post(`/integrations/${p}/disconnect`); toast.success(`${p} disconnected`); load(); };
  const sync = async (p) => {
    try { const { data } = await api.post(`/integrations/${p}/sync`); toast.success(data.message); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const download = async (p) => {
    try {
      const res = await api.get(`/integrations/${p}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${p}_payroll.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success(`${p} file downloaded`);
    } catch { toast.error("Export failed"); }
  };

  const meta = PROVIDERS.find((p) => p.key === dialog);
  const inputCls = "mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition";

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Payroll integrations</h1>
      <p className="text-slate-400 text-sm mb-4">Connect a provider and push payroll, or download a ready-to-import file.</p>
      <div className="mb-6 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
        <b>Note:</b> Formatted file exports are fully functional. Direct API sync is simulated — live delivery requires approved partner credentials from each provider.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PROVIDERS.map((p) => {
          const connected = status[p.key]?.connected;
          return (
            <div key={p.key} data-testid={`integration-card-${p.key}`} className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-extrabold" style={{ backgroundColor: p.color }}>{p.name[0]}</div>
                {connected ? (
                  <span className="inline-flex items-center gap-1 text-xs font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> Connected</span>
                ) : (
                  <span className="text-xs font-mono text-slate-500 border border-slate-700 rounded-full px-2.5 py-1">Not connected</span>
                )}
              </div>
              <h3 className="font-bold text-lg">{p.name}</h3>
              <p className="text-sm text-slate-400 mt-1 flex-1">{p.blurb}</p>
              <div className="mt-4 space-y-2">
                {connected ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid={`sync-${p.key}`} onClick={() => sync(p.key)} className="rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"><RefreshCw className="w-4 h-4" /> Sync</button>
                    <button data-testid={`disconnect-${p.key}`} onClick={() => disconnect(p.key)} className="rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"><Link2Off className="w-4 h-4" /> Disconnect</button>
                  </div>
                ) : (
                  <button data-testid={`connect-${p.key}`} onClick={() => { setDialog(p.key); setForm({}); }} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"><Plug className="w-4 h-4" /> Connect</button>
                )}
                <button data-testid={`export-${p.key}`} onClick={() => download(p.key)} className="w-full rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"><Download className="w-4 h-4" /> Download {p.name} file</button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle>Connect {meta?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {meta?.fields.map(([k, label]) => (
              <div key={k}>
                <label className="text-sm font-medium text-slate-300">{label}</label>
                <input data-testid={`integration-field-${k}`} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls} />
              </div>
            ))}
            <p className="text-xs text-slate-500">Credentials are stored for building provider-formatted exports.</p>
          </div>
          <DialogFooter>
            <button data-testid="integration-connect-submit" onClick={connect} disabled={busy} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold disabled:opacity-60 transition-colors">{busy ? "Connecting…" : "Connect"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
